import { useState } from 'react';

import { readDemoMaskMode, toggleDemoMask, type DemoMaskMode } from './demo-mask';

const ICON_STROKE = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.7,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

/** An open eye — the data is readable (the mask is off). */
function IconEye() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
      <path d="M3 12s3.6-5.5 9-5.5S21 12 21 12s-3.6 5.5-9 5.5S3 12 3 12Z" {...ICON_STROKE} />
      <circle cx="12" cy="12" r="2.4" {...ICON_STROKE} />
    </svg>
  );
}

/** A crossed-out eye — the mask is on. */
function IconEyeOff() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
      <path d="M3 12s3.6-5.5 9-5.5S21 12 21 12s-3.6 5.5-9 5.5S3 12 3 12Z" {...ICON_STROKE} />
      <circle cx="12" cy="12" r="2.4" {...ICON_STROKE} />
      <path d="M4.5 19.5 19.5 4.5" {...ICON_STROKE} />
    </svg>
  );
}

export interface PdnToggleProps {
  /** Reads the resolved mode — injectable so the unit spec can pin the markup. */
  read?: () => DemoMaskMode;
  /** The click handler — injectable for the same reason. */
  onToggle?: (current: boolean) => boolean;
}

/**
 * T.Z112 — the header button: the ONLY switch of the personal-data mask
 * (`./demo-mask`). Offered to every authenticated session in every environment
 * (the topbar is the authenticated shell); the stand default it boots with is
 * `readDemoMaskMode`'s business — ON on `cb-manager.local`, OFF elsewhere.
 *
 * T.Z118 — ICON-ONLY (eye / crossed-out eye): the «ПДн» caption is gone, the
 * meaning lives in `aria-label` + `title`, which also tell the user what the
 * NEXT click does («… — показать»). `aria-pressed` stays the state pin the
 * unit spec asserts on. The default `onToggle` persists to
 * `localStorage['demo-mask']`, flips the root class and starts/stops the
 * masking engine, so a click applies immediately — no reload.
 */
export function PdnToggle({ read = () => readDemoMaskMode(), onToggle = toggleDemoMask }: PdnToggleProps = {}) {
  const [on, setOn] = useState(() => read() === 'on');
  const label = on ? 'Персональные данные скрыты — показать' : 'Скрыть персональные данные';

  return (
    <button
      type="button"
      className="icon-btn pdn-toggle"
      onClick={() => setOn((current) => onToggle(current))}
      aria-pressed={on}
      aria-label={label}
      title={label}
      data-testid="pdn-toggle"
    >
      {on ? <IconEyeOff /> : <IconEye />}
    </button>
  );
}
